import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  CalendarMonth,
  DateRange,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMarketKPIs, useKPIDetail } from '../hooks/useMarketData';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const CLASSIFICATIONS = [
  'All',
  'Luxury',
  'Upper Upscale',
  'Upscale',
  'Upper Midscale',
  'Midscale',
  'Economy'
];

const KPITypeDetails = {
  occupancy: {
    title: 'Occupancy Rate',
    unit: '%',
    description: 'The percentage of available rooms that are occupied',
    multiplier: 100, // Occupancy comes as decimal, needs to be multiplied
  },
  adr: {
    title: 'Average Daily Rate',
    unit: 'USD',
    description: 'The average rental income per occupied room',
    multiplier: 1,
  },
  revpar: {
    title: 'Revenue Per Available Room',
    unit: 'USD',
    description: 'Revenue divided by the total number of available rooms',
    multiplier: 1,
  },
  rooms: {
    title: 'Rooms',
    unit: 'rooms',
    description: 'Total number of rooms in inventory',
    multiplier: 1,
  },
  demand: {
    title: 'Room Demand',
    unit: 'nights',
    description: 'Total number of room nights sold',
    multiplier: 1,
  },
};

export const KPIDetail: React.FC = () => {
  const navigate = useNavigate();
  const { marketId, kpiType } = useParams<{ marketId: string; kpiType: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'current' | 'ytd' | 'both'>('both');
  const [years, setYears] = useState<1 | 3 | 5 | 10>(5);
  const [classification, setClassification] = useState(searchParams.get('classification') || 'All');

  // Update URL when classification changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (classification !== 'All') {
      params.set('classification', classification);
    } else {
      params.delete('classification');
    }
    setSearchParams(params, { replace: true });
  }, [classification]);

  const { data: marketData, isLoading: marketLoading, error: marketError } = useMarketKPIs(
    marketId ? parseInt(marketId) : null,
    classification
  );

  const { data: detailData, isLoading: detailLoading, error: detailError } = useKPIDetail(
    marketId ? parseInt(marketId) : null,
    kpiType as 'occupancy' | 'adr' | 'revpar' | 'rooms' | 'demand',
    years,
    classification
  );

  // Validate KPI type before any conditional returns
  const isValidKpiType = kpiType && ['occupancy', 'adr', 'revpar', 'rooms', 'demand'].includes(kpiType);
  const kpiDetailsTemp = isValidKpiType ? KPITypeDetails[kpiType as keyof typeof KPITypeDetails] : null;

  // Set page title (must be called before any returns - Rules of Hooks)
  usePageTitle(
    kpiDetailsTemp && marketData?.market
      ? `${kpiDetailsTemp.title} - ${marketData.market.name}`
      : 'KPI Detail'
  );

  // Now we can do conditional returns
  if (!isValidKpiType || !kpiDetailsTemp) {
    return <ErrorMessage message="Invalid KPI type" />;
  }

  // Type assertion: after validation, we know these are defined
  const kpiDetails = kpiDetailsTemp;

  if (marketLoading || detailLoading) {
    return <LoadingSpinner message={`Loading ${kpiDetails.title} data...`} />;
  }

  if (marketError || !marketData || detailError || !detailData) {
    return <ErrorMessage message={`Failed to load ${kpiDetails.title} data`} />;
  }

  const { market, kpis } = marketData;
  const kpiData = kpis[kpiType as keyof typeof kpis];
  const { timeseries, statistics, benchmarks } = detailData;

  const getTrendIcon = (change: number | null | undefined) => {
    if (change === null || change === undefined) return <TrendingFlat sx={{ fontSize: 16 }} />;
    if (change > 1) return <TrendingUp color="success" sx={{ fontSize: 16 }} />;
    if (change < -1) return <TrendingDown color="error" sx={{ fontSize: 16 }} />;
    return <TrendingFlat sx={{ fontSize: 16 }} />;
  };

  const getChangeColor = (change: number | null | undefined) => {
    if (change === null || change === undefined) return 'text.secondary';
    return change >= 0 ? 'success.main' : 'error.main';
  };

  const formatValue = (value: number | null | undefined, unit?: string) => {
    if (value === null || value === undefined) return '--';

    if (unit === '%') return `${value.toFixed(1)}%`;
    if (unit === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
    if (unit === 'nights' || unit === 'rooms') {
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return value.toFixed(2);
  };

  const formatChartValue = (value: number) => {
    if (kpiDetails.unit === '%') {
      // Value is already multiplied by 100 in chartData, just format it
      return `${value.toFixed(0)}%`;
    }
    if (kpiDetails.unit === 'USD') {
      return value.toFixed(2);
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  // Prepare chart data
  const chartData = timeseries.map((item: any) => ({
    date: item.date,
    value: item.value ? item.value * kpiDetails.multiplier : null,
    yoyChange: item.yoy_change,
  }));

  // Render statistics card
  const renderStatisticsCard = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Statistical Analysis ({years} Years)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Average
            </Typography>
            <Typography variant="h6">
              {formatValue(
                statistics.average ? statistics.average * kpiDetails.multiplier : null,
                kpiDetails.unit
              )}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              5-Year CAGR
            </Typography>
            <Typography variant="h6" color={getChangeColor(statistics.cagr_5yr)}>
              {statistics.cagr_5yr !== null ? `${statistics.cagr_5yr.toFixed(1)}%` : '--'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Minimum
            </Typography>
            <Typography variant="body1">
              {formatValue(
                statistics.min ? statistics.min * kpiDetails.multiplier : null,
                kpiDetails.unit
              )}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Maximum
            </Typography>
            <Typography variant="body1">
              {formatValue(
                statistics.max ? statistics.max * kpiDetails.multiplier : null,
                kpiDetails.unit
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Seasonality (StDev σ)
            </Typography>
            <Typography variant="body1">
              {formatValue(
                statistics.std_dev ? statistics.std_dev * kpiDetails.multiplier : null,
                kpiDetails.unit
              )}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Render benchmarks card
  const renderBenchmarksCard = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Benchmarking
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary">
            Country Average
          </Typography>
          <Typography variant="h6">
            {formatValue(
              benchmarks.country_average ? benchmarks.country_average * kpiDetails.multiplier : null,
              kpiDetails.unit
            )}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary">
            Continent Average
          </Typography>
          <Typography variant="h6">
            {formatValue(
              benchmarks.continent_average ? benchmarks.continent_average * kpiDetails.multiplier : null,
              kpiDetails.unit
            )}
          </Typography>
        </Box>
        {benchmarks.competitive_set && benchmarks.competitive_set.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Competitive Set (Same Country)
            </Typography>
            {benchmarks.competitive_set.map((comp: any, index: number) => (
              <Box key={index} display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                  {comp.market}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatValue(comp.value ? comp.value * kpiDetails.multiplier : null, kpiDetails.unit)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render current values card
  const renderCurrentValuesCard = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Current Performance
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {(viewMode === 'current' || viewMode === 'both') && kpiData?.current_month && (
          <Box mb={3}>
            <Chip label={kpiData.current_month.period} size="small" sx={{ mb: 1 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {formatValue(
                  kpiData.current_month.value,
                  kpiDetails.unit
                )}
              </Typography>
              {kpiData.current_month.change_percent !== null && kpiData.current_month.change_percent !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(kpiData.current_month.change_percent)}
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color={getChangeColor(kpiData.current_month.change_percent)}
                  >
                    {kpiData.current_month.change_percent > 0 ? '+' : ''}
                    {kpiData.current_month.change_percent.toFixed(1)}% YoY
                  </Typography>
                </Box>
              )}
            </Box>
            {kpiData.current_month.previous_year !== null && kpiData.current_month.previous_year !== undefined && (
              <Typography variant="body2" color="text.secondary">
                Previous Year: {formatValue(kpiData.current_month.previous_year, kpiDetails.unit)}
              </Typography>
            )}
          </Box>
        )}

        {/* 3-Month Average - Only for KPIMetric types (occupancy, adr, revpar) */}
        {(kpiType === 'occupancy' || kpiType === 'adr' || kpiType === 'revpar') && kpiData && 'three_month' in kpiData && kpiData.three_month && (
          <Box mb={3}>
            <Divider sx={{ my: 2 }} />
            <Chip label={kpiData.three_month.period} size="small" sx={{ mb: 1 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight="bold">
                {formatValue(kpiData.three_month.value, kpiDetails.unit)}
              </Typography>
              {kpiData.three_month.change_percent !== null && kpiData.three_month.change_percent !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(kpiData.three_month.change_percent)}
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color={getChangeColor(kpiData.three_month.change_percent)}
                  >
                    {kpiData.three_month.change_percent > 0 ? '+' : ''}
                    {kpiData.three_month.change_percent.toFixed(1)}% YoY
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* YTD - Not for rooms */}
        {kpiType !== 'rooms' && kpiData?.ytd && (
          <Box mb={3}>
            <Divider sx={{ my: 2 }} />
            <Chip label={kpiData.ytd.period} size="small" sx={{ mb: 1 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {formatValue(kpiData.ytd.value, kpiDetails.unit)}
              </Typography>
              {kpiData.ytd.change_percent !== null && kpiData.ytd.change_percent !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(kpiData.ytd.change_percent)}
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color={getChangeColor(kpiData.ytd.change_percent)}
                  >
                    {kpiData.ytd.change_percent > 0 ? '+' : ''}
                    {kpiData.ytd.change_percent.toFixed(1)}% YoY
                  </Typography>
                </Box>
              )}
            </Box>
            {kpiData.ytd.previous_year !== null && kpiData.ytd.previous_year !== undefined && (
              <Typography variant="body2" color="text.secondary">
                Previous Year YTD: {formatValue(kpiData.ytd.previous_year, kpiDetails.unit)}
              </Typography>
            )}
          </Box>
        )}

        {/* 12-Month Average - Only for KPIMetric types (occupancy, adr, revpar) */}
        {(kpiType === 'occupancy' || kpiType === 'adr' || kpiType === 'revpar') && kpiData && 'twelve_month' in kpiData && kpiData.twelve_month && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Chip label={kpiData.twelve_month.period} size="small" sx={{ mb: 1 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight="bold">
                {formatValue(kpiData.twelve_month.value, kpiDetails.unit)}
              </Typography>
              {kpiData.twelve_month.change_percent !== null && kpiData.twelve_month.change_percent !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(kpiData.twelve_month.change_percent)}
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color={getChangeColor(kpiData.twelve_month.change_percent)}
                  >
                    {kpiData.twelve_month.change_percent > 0 ? '+' : ''}
                    {kpiData.twelve_month.change_percent.toFixed(1)}% YoY
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render historical chart
  const renderHistoricalChart = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Historical Trend
          </Typography>
          <ToggleButtonGroup
            value={years}
            exclusive
            onChange={(_, value) => value && setYears(value)}
            size="small"
          >
            <ToggleButton value={1}>1Y</ToggleButton>
            <ToggleButton value={3}>3Y</ToggleButton>
            <ToggleButton value={5}>5Y</ToggleButton>
            <ToggleButton value={10}>10Y</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                const [year, month] = value.split('-');
                return `${month}/${year.slice(2)}`;
              }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => formatChartValue(value)}
            />
            <Tooltip
              formatter={(value: any) => [
                formatValue(value / kpiDetails.multiplier, kpiDetails.unit),
                kpiDetails.title,
              ]}
              labelFormatter={(label) => {
                const [year, month] = label.split('-');
                const monthNames = [
                  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                ];
                return `${monthNames[parseInt(month) - 1]} ${year}`;
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#1976d2"
              strokeWidth={2}
              dot={false}
              name={kpiDetails.title}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  return (
    <Box pb={10}>
      {/* Header */}
      <Box px={2} pt={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => {
            const params = classification !== 'All' ? `?classification=${encodeURIComponent(classification)}` : '';
            navigate(`/market/${marketId}${params}`);
          }}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h5" fontWeight="bold">
              {kpiDetails.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {market.name} • {market.country}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {kpiDetails.description}
        </Typography>
      </Box>

      {/* Hotel Class Selector */}
      <Box px={2} mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">
            Hotel Class:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              displayEmpty
            >
              {CLASSIFICATIONS.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Controls */}
      {kpiType === 'demand' && (
        <Box px={2} mb={3}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="current">
              <CalendarMonth sx={{ mr: 0.5, fontSize: 20 }} />
              Current
            </ToggleButton>
            <ToggleButton value="ytd">
              <DateRange sx={{ mr: 0.5, fontSize: 20 }} />
              YTD
            </ToggleButton>
            <ToggleButton value="both">Both</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Content */}
      <Box px={2}>
        <Grid container spacing={2}>
          {/* Current Values */}
          <Grid item xs={12}>
            {renderCurrentValuesCard()}
          </Grid>

          {/* Historical Chart */}
          <Grid item xs={12}>
            {renderHistoricalChart()}
          </Grid>

          {/* Statistics */}
          <Grid item xs={12} md={6}>
            {renderStatisticsCard()}
          </Grid>

          {/* Benchmarks */}
          <Grid item xs={12} md={6}>
            {renderBenchmarksCard()}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
